import { FormTemplate, SearchModel, SearchType } from '@nrwl/rx-models';
import { FormCategory } from 'libs/models/form/form-category';
import { SelectionModel } from '@angular/cdk/collections';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { State } from './reducers/index';
export * from './root-store.module';
export * from './root-store.selectors';
export * from './root-store.state';
export * from './app-context.token';

// Selector functions
const getFormsManagerState = createFeatureSelector<State>('forms-manager');
const createEmptyFormsManagerState = (): State => ({
  category: {
    selectedCategory: null,
    selectedCategories: new SelectionModel<number>(true, []),
    currentCategory: null,
    currentCategoryId: 0,
    categories: [],
    entryCategory: null,
    categoriesLoaded: false,
    busy: false,
    error: '',
    globalCategories: [],
    globalCategoriesCount: 0,
    globalCategoriesLoaded: false,
    globalCategoriesBusy: false,
    globalCategoriesPage: 1,
    globalCategoriesNext: null,
    globalCategoriesHasMore: true,
    globalCategoriesLoadingMore: false,
    accountCategories: [],
    accountCategoriesCount: 0,
    accountCategoriesLoaded: false,
    accountCategoriesBusy: false,
    accountCategoriesPage: 1,
    accountCategoriesNext: null,
    accountCategoriesHasMore: true,
    accountCategoriesLoadingMore: false,
    dialogAccountCategories: [],
    dialogAccountCategoriesLoaded: false,
    dialogAccountCategoriesLoading: false,
    dialogGlobalCategories: [],
    dialogGlobalCategoriesLoaded: false,
    dialogGlobalCategoriesLoading: false,
  },
  template: {
    forms: [],
    practice: [],
    archive: [],
    practice_archived_templates: [],
    formsLoaded: false,
    archiveBusy: false,
    currentForm: null,
    currentFormId: 0,
    deleteForms: null,
    selectedForm: null,
    search: null,
    busyCategories: new SelectionModel<number>(true, []),
    busyTemplates: new SelectionModel<number>(true, []),
    uploaded: [],
    uploadedBusy: false,
    busy: new SelectionModel<SearchType>(true, []),
    previewOpened: false,
    error: '',
    loading: false,
  },
  error: {
    event: null,
  },
});
const getSafeFormsManagerState = createSelector(
  getFormsManagerState,
  (state) => state ?? createEmptyFormsManagerState(),
);

export const confirmAction = createSelector(getSafeFormsManagerState, (state) => state);

export const getState = createSelector(getSafeFormsManagerState, (state) => state);

export const formsLoaded = createSelector(
  getSafeFormsManagerState,
  (state) => state.template.formsLoaded,
);

export const isArchivedBusy = createSelector(
  getSafeFormsManagerState,
  (state) => state.template.archiveBusy,
);

export const isAccountArchivedBusy = createSelector(
  getSafeFormsManagerState,
  (state) => state.template.archiveBusy,
);

/** @deprecated Use getPaginatedGlobalCategories instead */
export const getGlobalCategories = (props: any = null) =>
  createSelector(getSafeFormsManagerState, (state) => {
    const allCategories = [...state.category.globalCategories, ...state.category.accountCategories];
    // Check if search model supplied
    if (props) {
      // Get search model of specific type
      const search = (state.template.search || []).find((model) => model.type === props.searchType);

      // Filter categories if searching
      if (search && search.keyword.length > 1) {
        if (!search.result) {
          return null;
        }

        // Return categories from found templates
        const filteredCategories = allCategories.filter((categ) => {
          return categ.ofType(props.searchType) && search.result.categories.indexOf(+categ.id) > -1;
        });

        // Update total templates counter per category
        const templatesFormModel = (state.template.search || []).find(
          (model) => model.type === props.searchType,
        );
        let foundTempaltes = [];
        if (templatesFormModel) {
          foundTempaltes = (search.result || {}).templates || [];

          return filteredCategories.map((categ) => {
            return {
              ...categ,
              forms_count: foundTempaltes.filter((templ: any) => templ.category === categ.id)
                .length,
            };
          });
        }
        return filteredCategories;
      }
    }

    // Return all global categories
    return allCategories.filter((categ) => {
      return categ.ofType(SearchType.GlobalLibrary);
    });
  });

/** @deprecated Use getPaginatedAccountCategories instead */
export const getAccountCategories = (props: any = null) =>
  createSelector(getSafeFormsManagerState, (state) => {
    const allCategories = [...state.category.globalCategories, ...state.category.accountCategories];
    const search = (state.template.search || []).find((model) => model.type === props.searchType);

    // Filter categories if searching
    if (search && search.keyword.length > 1) {
      // Results not ready yet
      if (!search.result) {
        return null;
      }

      // Return categories to display
      return allCategories.filter((categ) => {
        return (
          categ.is_stock &&
          categ.is_stock[0] === false &&
          search.result.categories.indexOf(categ.id) > -1
        );
      });
    } else {
      // Return all account categories
      return allCategories.filter((categ) => {
        return categ.ofType(SearchType.AccountLibrary);
      });
    }
  });

export const getAllCategories = createSelector(getSafeFormsManagerState, (state) => [
  ...state.category.globalCategories,
  ...state.category.accountCategories,
]);

export const getSelectedCategories = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.selectedCategories,
);

/** @deprecated Use getGlobalCategoriesBusy / getAccountCategoriesBusy instead */
export const getCategoriesBusyStatus = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.busy,
);

export const getBusyCategories = createSelector(
  getSafeFormsManagerState,
  (state) => state.template.busyCategories,
);

export const getBusyTemplates = createSelector(
  getSafeFormsManagerState,
  (state) => state.template.busyTemplates,
);

export const getLoading = createSelector(
  getSafeFormsManagerState,
  (state) => state.template.loading,
);

export const getPreviewOpened = createSelector(
  getSafeFormsManagerState,
  (state) => state.template.previewOpened,
);

export const getSelectedTemplate = createSelector(
  getSafeFormsManagerState,
  (state) => state.template.selectedForm,
);

export const getFormTemplates = (props) =>
  createSelector(getSafeFormsManagerState, (state) => {
    if (!props) {
      return state.template.forms;
    }

    let source = state.template.forms;

    const search = (state.template.search || []).find((model) => model.type === props.searchType);

    if (search) {
      source = (search.result || {}).templates || [];
    }

    // Filter templates by category
    if (props && props.category) {
      const result = source.filter(
        (form) => form.category === props.category.id && form.ofType(props.searchType),
      );
      return result;
    }
    return source;
  });

export const getSearchModel = (props) =>
  createSelector(getSafeFormsManagerState, (state) => {
    if (state.template.search) {
      return state.template.search.find((model: SearchModel) => model.type === props.searchType);
    }
    return null;
  });

export const getGlobalArchivedTemplates = (props) =>
  createSelector(getSafeFormsManagerState, (state) => {
    // TODO: IMPORTANT: Verify RWE-298

    let source = state.template.archive;

    const search = (state.template.search || []).find((model) => model.type === props.searchType);

    if (search) {
      source = (search.result || {}).templates || [];
    }

    let result = source.filter((item) => {
      return item.is_stock && item.is_stock[0] === true;
    });

    // WARNING: HACK: Back-end does not support pagination
    if (result.length > 100) {
      result = result.splice(0, 50);
    }
    return result;
  });

export const isGlobalArchivedTemplatesBusy = createSelector(getSafeFormsManagerState, (state) => {
  // TODO: IMPORTANT: Verify RWE-298
  return state.template.archiveBusy;
  // if(state.template.archive){
  //     return state.template.archive.filter(item => {
  //         return item.is_stock && item.is_stock[0] === true
  //     })
  // }
});

export const getArchiveTemplates = (props) =>
  createSelector(getSafeFormsManagerState, (state) => {
    // TODO: IMPORTANT: Verify RWE-298
    let source = state.template.archive;

    const search = (state.template.search || []).find((model) => model.type === props.searchType);

    if (search) {
      source = (search.result || {}).templates || [];
    }

    return source.filter((item: FormTemplate) => {
      return item.ofType(props.searchType);
    });
  });

export const getUploadedTemplates = (props) =>
  createSelector(getSafeFormsManagerState, (state) => {
    let source = state.template.uploaded;

    const search = (state.template.search || []).find((model) => model.type === props.searchType);

    if (search) {
      source = (search.result || {}).templates || [];
    }
    const result = source.filter((item: FormTemplate) => {
      return item.ofType(props.searchType);
    });

    return result;
  });

export const getUploadedArchivedTemplates = (props) =>
  createSelector(getSafeFormsManagerState, (state) => {
    let source = state.template.uploaded;

    const search = (state.template.search || []).find((model) => model.type === props.searchType);

    if (search) {
      source = (search.result || {}).templates || [];
    }

    return source;
  });

export const isUploadedBusy = createSelector(
  getSafeFormsManagerState,
  (state) => state.template.uploadedBusy,
);

// TODO: DEPRICATED: Detach all below and clean up

export const getForms = createSelector(getSafeFormsManagerState, (state) => state.template.forms);

export const getFormById = createSelector(getSafeFormsManagerState, (state, id) =>
  state.template.forms.find((q) => q.id === id),
);

export const getCurrentFormId = createSelector(
  getSafeFormsManagerState,
  (state) => state.template.currentFormId,
);

export const getCurrentForm = createSelector(
  getSafeFormsManagerState,
  getCurrentFormId,
  (state, currentFormId) => {
    let result: any = new FormTemplate();
    if (currentFormId > 0) {
      result = state.template.forms.find((p) => p.id === currentFormId);
    }
    return result;
  },
);

export const isPracticeTemplatesBusy = createSelector(
  getSafeFormsManagerState,
  (state) => state.template.busyTemplates,
);

export const getPracticeTemplates = (props) =>
  createSelector(getSafeFormsManagerState, (state) => {
    if (!props) {
      return state.template.practice;
    }

    let source = state.template.practice;

    const search = (state.template.search || []).find((model) => model.type === props.searchType);

    if (search) {
      source = (search.result || {}).templates || [];
    }

    const result = source.filter((template: FormTemplate) => {
      return template.ofType(props.searchType);
    });

    return result;
  });

export const getPracticeLibraryArchivedTemplates = (props) =>
  createSelector(getSafeFormsManagerState, (state) => {
    let source = state.template.archive;

    const search = (state.template.search || []).find((model) => model.type === props.searchType);

    if (search) {
      source = (search.result || {}).templates || [];
    }

    return source.filter((item: FormTemplate) => {
      return item.ofType(props.searchType);
    });
  });

export const getPracticeLibraryBusy = (props) =>
  createSelector(getSafeFormsManagerState, (state) => {
    const types = Array.from(state.template.busy['_selection'] || []);
    return types.includes(props.searchType);
  });

// Global categories (paginated)
export const getPaginatedGlobalCategories = createSelector(getSafeFormsManagerState, (state) => {
  const search = (state.template.search || []).find(
    (model) => model.type === SearchType.GlobalLibrary,
  );

  if (search && search.keyword && search.keyword.length > 1) {
    if (!search.result) {
      return null;
    }

    // `state.category.globalCategories` only contains the pages loaded so far. If a
    // matched template belongs to a category from a not-yet-loaded page, filtering against
    // that local array silently drops it from the results. Build the category list from
    // `search.result.categories` (the full set of matched category ids returned by the
    // separate search request) instead, falling back to a lightweight placeholder (using
    // `category_name` from the template) for any id not present locally.
    const categoriesById = new Map(
      state.category.globalCategories.map((categ) => [categ.id, categ]),
    );
    const foundTemplates = (search.result || {}).templates || [];
    const resultCategoryIds: number[] = search.result.categories || [];

    return resultCategoryIds.map((id) => {
      const categ = categoriesById.get(+id);
      const templatesInCategory = foundTemplates.filter((templ: any) => templ.category === id);
      const name = categ?.name || (templatesInCategory[0] as any)?.category_name || `Folder #${id}`;

      return {
        ...(categ || new FormCategory({ id, name, is_stock: [true, 'None'], is_deleted: false })),
        forms_count: templatesInCategory.length,
      };
    });
  }

  return state.category.globalCategories;
});

export const getLoadedGlobalCategories = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.globalCategories,
);

export const getGlobalCategoriesCount = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.globalCategoriesCount,
);

export const getGlobalCategoriesLoaded = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.globalCategoriesLoaded,
);

export const getGlobalCategoriesBusy = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.globalCategoriesBusy,
);

export const getGlobalCategoriesHasMore = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.globalCategoriesHasMore,
);

export const getGlobalCategoriesLoadingMore = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.globalCategoriesLoadingMore,
);

// Account categories (paginated)
export const getPaginatedAccountCategories = createSelector(getSafeFormsManagerState, (state) => {
  const search = (state.template.search || []).find(
    (model) => model.type === SearchType.AccountLibrary,
  );

  if (search && search.keyword && search.keyword.length > 1) {
    if (!search.result) {
      return null;
    }

    // See getPaginatedGlobalCategories above for why we build the category list from
    // search.result.categories (the full set of matched ids from the separate search
    // request) instead of filtering the local paginated array.
    const categoriesById = new Map(
      state.category.accountCategories.map((categ) => [categ.id, categ]),
    );
    const foundTemplates = (search.result || {}).templates || [];
    const resultCategoryIds: number[] = search.result.categories || [];

    return resultCategoryIds.map((id) => {
      const categ = categoriesById.get(+id);
      const templatesInCategory = foundTemplates.filter((templ: any) => templ.category === id);
      const name = categ?.name || (templatesInCategory[0] as any)?.category_name || `Folder #${id}`;

      return {
        ...(categ || new FormCategory({ id, name, is_stock: [false, 'None'], is_deleted: false })),
        forms_count: templatesInCategory.length,
      };
    });
  }

  return state.category.accountCategories;
});

export const getLoadedAccountCategories = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.accountCategories,
);

export const getAccountCategoriesCount = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.accountCategoriesCount,
);

export const getAccountCategoriesLoaded = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.accountCategoriesLoaded,
);

export const getAccountCategoriesBusy = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.accountCategoriesBusy,
);

export const getAccountCategoriesHasMore = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.accountCategoriesHasMore,
);

export const getAccountCategoriesLoadingMore = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.accountCategoriesLoadingMore,
);

export const getDialogAccountCategories = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.dialogAccountCategories,
);

export const getDialogAccountCategoriesLoading = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.dialogAccountCategoriesLoading,
);

export const getDialogGlobalCategories = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.dialogGlobalCategories,
);

export const getDialogGlobalCategoriesLoading = createSelector(
  getSafeFormsManagerState,
  (state) => state.category.dialogGlobalCategoriesLoading,
);
